import { useEffect, useRef, useCallback } from 'react';
import { createFractalWorker } from './createFractalWorker';
import { planPan, copyPanPixels } from './fractalPixels';
import { isAnalyticZeroTile } from './cpuInteriorCertificate';
import { fullConjugateRowMap, fillConjugateRows } from './fractalConjugateRows';

const MIN_PIXELS_FOR_WORKER = 80000;
// At most 4096 pixels of obsolete work per busy worker before cancellation
// takes effect. No worker receives another message until its tile finishes.
const TILE_WIDTH = 128;
const TILE_HEIGHT = 32;
let workers = [];
let requestId = 0;
let workerFailed = false;
let pumping = false;
const owners = new Set();
let readyOwners = [];
const exactStats = { started: 0, completed: 0, canceled: 0, failed: 0,
  active: 0, peakActive: 0, activeSourceBytes: 0, peakSourceBytes: 0,
  activeDestinationBytes: 0, peakDestinationBytes: 0,
  peakAssemblyBytes: 0 };

function releaseExactJob(job) {
  if (!job?.exactDestinationBytes) return;
  exactStats.active--;
  exactStats.activeSourceBytes -= job.exactSourceBytes;
  exactStats.activeDestinationBytes -= job.exactDestinationBytes;
  job.exactSourceBytes = 0;
  job.exactDestinationBytes = 0;
}

export function getCpuExactWorkerMetrics() { return { ...exactStats }; }

function canUseWorkers() {
  if (typeof Worker === 'undefined') return false;
  return typeof window === 'undefined' ||
    (window.location.protocol !== 'file:' && window.location.origin !== 'null');
}

function abortError(message = 'Render superseded') {
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
}

function rejectJob(job, error) {
  if (!job || job.settled) return;
  job.settled = true;
  if (job.type === 'renderExactPan') {
    if (error?.name === 'AbortError') exactStats.canceled++;
    else exactStats.failed++;
    releaseExactJob(job);
  }
  job.reject(error);
  // Release incomplete images immediately; in-flight tiles are discarded.
  job.pixels = null;
  job.conjugateMap = null;
  if (job.params && job.type === 'renderExactPan') job.params.sourcePixels = null;
  job.cleanX = null;
  job.cleanY = null;
  job.regions = [];
  job.tiles = [];
}

function cancelOwner(owner, error) {
  if (!owner) return;
  rejectJob(owner.pending, error);
  owner.pending = null;
  rejectJob(owner.active, error);
  if (owner.active?.inFlight === 0) owner.active = null;
  readyOwners = readyOwners.filter(candidate => candidate !== owner);
}

function stopPool(error) {
  workers.forEach(slot => {
    slot.task = null;
    slot.worker.terminate();
  });
  workers = [];
  owners.forEach(owner => {
    cancelOwner(owner, error);
    owner.active = null;
  });
  readyOwners = [];
}

function handleWorkerError() {
  workerFailed = true;
  stopPool(new Error('Fractal worker failed'));
}

function ensurePool() {
  if (workers.length > 0) return;
  if (workerFailed || !canUseWorkers()) throw new Error('Workers unavailable');
  try {
    const size = Math.min(navigator.hardwareConcurrency || 4, 8);
    for (let i = 0; i < size; i++) {
      const slot = { worker: createFractalWorker(), task: null };
      workers.push(slot);
      slot.worker.onmessage = ({ data: { id, type, data, error } }) => {
        completeTile(slot, id, data, type === 'error' ? new Error(error) : null);
      };
      slot.worker.onerror = handleWorkerError;
      slot.worker.onmessageerror = handleWorkerError;
    }
  } catch (error) {
    workerFailed = true;
    stopPool(error);
    throw error;
  }
}

function ready(owner) {
  const job = owner.active;
  if (job && !job.settled && job.nextTile < job.tiles.length && !readyOwners.includes(owner)) {
    readyOwners.push(owner);
  }
}

function startPending(owner) {
  if (owner.active || !owner.pending) return;
  const job = owner.pending;
  owner.pending = null;
  owner.active = job;
  const { canvasWidth: width, canvasHeight: height } = job.params;
  try {
    const exact = job.type === 'renderExactPan';
    const plan = job.type === 'renderPan' ? planPan(job.params) : {
      regions: [{ x: 0, y: 0, width, height }]
    };
    job.pixelOffsetX = plan.pixelOffsetX;
    job.pixelOffsetY = plan.pixelOffsetY;
    job.pixels = job.type === 'renderFull' || exact
      ? new Uint8ClampedArray(width * height * 4) : null;
    if (exact) {
      const { sourcePixels, dx, dy, cleanX, cleanY } = job.params;
      if (!(sourcePixels instanceof Uint8ClampedArray) || sourcePixels.length !== width * height * 4 ||
          !(cleanX instanceof Uint8Array) || cleanX.length !== width ||
          !(cleanY instanceof Uint8Array) || cleanY.length !== height) {
        throw new Error('Invalid exact-pan source or coordinate masks');
      }
      job.cleanX = cleanX;
      job.cleanY = cleanY;
      copyPanPixels(sourcePixels, job.pixels, width, height, dx, dy);
      job.exactSourceBytes = sourcePixels.byteLength;
      job.exactDestinationBytes = job.pixels.byteLength;
      exactStats.started++;
      exactStats.active++;
      exactStats.activeSourceBytes += job.exactSourceBytes;
      exactStats.activeDestinationBytes += job.exactDestinationBytes;
      exactStats.peakActive = Math.max(exactStats.peakActive, exactStats.active);
      exactStats.peakSourceBytes = Math.max(exactStats.peakSourceBytes,
        exactStats.activeSourceBytes);
      exactStats.peakDestinationBytes = Math.max(exactStats.peakDestinationBytes,
        exactStats.activeDestinationBytes);
      exactStats.peakAssemblyBytes = Math.max(exactStats.peakAssemblyBytes,
        exactStats.activeSourceBytes + exactStats.activeDestinationBytes);
    }
    job.tiles = [];
    job.dirtyPixels = 0;
    for (const region of plan.regions) {
      for (let y = region.y; y < region.y + region.height; y += TILE_HEIGHT) {
        for (let x = region.x; x < region.x + region.width; x += TILE_WIDTH) {
          const tile = { x, y, width: Math.min(TILE_WIDTH, region.x + region.width - x),
            height: Math.min(TILE_HEIGHT, region.y + region.height - y) };
          if (exact) {
            let cleanColumns = 0, cleanRows = 0;
            for (let j = x; j < x + tile.width; j++) cleanColumns += job.cleanX[j] === 1;
            for (let i = y; i < y + tile.height; i++) cleanRows += job.cleanY[i] === 1;
            tile.dirtyCount = tile.width * tile.height - cleanColumns * cleanRows;
            if (!tile.dirtyCount) continue;
            job.dirtyPixels += tile.dirtyCount;
          }
          // The full image starts transparent. Certified interior tiles need
          // no recurrence, worker message, transfer, or row copy.
          if (job.skipInteriorDispatch &&
              isAnalyticZeroTile({ canvasWidth: width, canvasHeight: height,
                xRange: job.params.newXRange || job.params.xRange,
                yRange: job.params.newYRange || job.params.yRange,
                startX: tile.x, startY: tile.y,
                regionWidth: tile.width, regionHeight: tile.height })) {
            continue;
          }
          job.tiles.push(tile);
        }
      }
    }
  } catch (error) {
    rejectJob(job, error);
  }
  finishJob(job);
  ready(owner);
}

function finishJob(job) {
  if (job.inFlight !== 0 || (!job.settled && job.nextTile < job.tiles.length)) return;
  if (!job.settled && job.type === 'renderFull' && job.conjugateMap) {
    try {
      fillConjugateRows(job.pixels, job.params.canvasWidth, job.conjugateMap.partners);
      job.conjugateMap = null;
    } catch (error) {
      rejectJob(job, error);
    }
  }
  if (!job.settled) {
    job.settled = true;
    if (job.type === 'renderExactPan') {
      exactStats.completed++;
      releaseExactJob(job);
    }
    job.resolve(job.type === 'renderExactPan' ? {
      pixels: job.pixels, workerTiles: job.tiles.length, computedPixels: job.dirtyPixels
    } : job.type === 'renderFull' ? job.pixels : {
      pixelOffsetX: job.pixelOffsetX, pixelOffsetY: job.pixelOffsetY, regions: job.regions
    });
  }
  job.owner.active = null;
  startPending(job.owner);
}

function completeTile(slot, id, pixels, error) {
  if (slot.task?.id !== id) return;
  const { job, tile } = slot.task;
  slot.task = null;
  job.inFlight--;
  if (!job.settled) {
    try {
      if (error) throw error;
      const expectedLength = job.type === 'renderExactPan'
        ? tile.dirtyCount * 4 : tile.width * tile.height * 4;
      if (pixels.length !== expectedLength) throw new Error('Invalid worker tile');
      if (job.type === 'renderExactPan') {
        const source32 = new Uint32Array(pixels.buffer, pixels.byteOffset, pixels.length / 4);
        const target32 = new Uint32Array(job.pixels.buffer);
        let sourceIndex = 0;
        for (let row = 0; row < tile.height; row++) {
          const cleanRow = job.cleanY[tile.y + row] === 1;
          for (let col = 0; col < tile.width; col++) {
            if (cleanRow && job.cleanX[tile.x + col] === 1) continue;
            target32[(tile.y + row) * job.params.canvasWidth + tile.x + col] = source32[sourceIndex++];
          }
        }
        if (sourceIndex !== tile.dirtyCount) throw new Error('Exact-pan sparse output count mismatch');
      } else if (job.type === 'renderFull') {
        for (let row = 0; row < tile.height; row++) {
          const start = row * tile.width * 4;
          job.pixels.set(pixels.subarray(start, start + tile.width * 4),
            ((tile.y + row) * job.params.canvasWidth + tile.x) * 4);
        }
      } else {
        job.regions.push({ ...tile, pixels });
      }
    } catch (failure) {
      rejectJob(job, failure);
    }
  }
  finishJob(job);
  pump();
}

function pump() {
  if (pumping) return;
  pumping = true;
  try {
    for (const slot of workers) {
      while (!slot.task && readyOwners.length) {
        // Round-robin between canvases so a large render cannot monopolize
        // every newly available worker.
        const owner = readyOwners.shift();
        const job = owner.active;
        if (!job || job.settled || job.nextTile >= job.tiles.length) continue;
        const tile = job.tiles[job.nextTile++];
        const id = ++requestId;
        slot.task = { id, job, tile };
        job.inFlight++;
        ready(owner);
        try {
          const exact = job.type === 'renderExactPan';
          const taskParams = {
            canvasWidth: job.params.canvasWidth, canvasHeight: job.params.canvasHeight,
            xRange: job.params.newXRange || job.params.xRange,
            yRange: job.params.newYRange || job.params.yRange,
            colorParams: job.params.colorParams,
            startX: tile.x, startY: tile.y, regionWidth: tile.width, regionHeight: tile.height,
            // A subarray structured clone can copy its entire backing mask
            // per tile. Send only the tile-sized mask bytes instead.
            ...(exact ? { cleanX: job.cleanX.slice(tile.x, tile.x + tile.width),
              cleanY: job.cleanY.slice(tile.y, tile.y + tile.height) } : {})
          };
          if (job.conjugateMap) taskParams.conjugateFullRows = true;
          slot.worker.postMessage({ id, type: exact ? 'renderSparse' : 'renderRegion',
            params: taskParams });
        } catch (error) {
          completeTile(slot, id, null, error);
        }
      }
    }
  } finally {
    pumping = false;
  }
}

// Hold one numerical grid for every omitted tile and every remaining worker
// message. Count and pan jobs keep their existing parameter route.
function snapshotEligibleFullParams(params, enabled) {
  if (!enabled) return params;
  const xRange = params.newXRange || params.xRange;
  const yRange = params.newYRange || params.yRange;
  if (!Array.isArray(xRange) || !Array.isArray(yRange) ||
      xRange.length !== 2 || yRange.length !== 2 ||
      ![...xRange, ...yRange].every(Number.isFinite)) return params;
  return { ...params, xRange: [...xRange], yRange: [...yRange],
    newXRange: null, newYRange: null,
    colorParams: params.colorParams && typeof params.colorParams === 'object'
      ? { ...params.colorParams } : params.colorParams };
}

function requestRender(owner, type, params) {
  if (!owner) return Promise.reject(abortError('Canvas unmounted'));
  return new Promise((resolve, reject) => {
    ensurePool();
    cancelOwner(owner, abortError());
    const skipInteriorDispatch = type === 'renderFull' &&
      params?.cpuInteriorDispatch === true && params.colorParams?.useMu !== false;
    const numericalYRange = params?.newYRange || params?.yRange;
    // Count and selected period-3 jobs cannot use real-axis row reuse. This
    // cheap guard avoids even calling the row helper on their common grids.
    const conjugateMap = type === 'renderFull' &&
      params?.colorParams?.useMu !== false &&
      Array.isArray(numericalYRange) && numericalYRange[0] < 0 &&
      numericalYRange[1] > 0
      ? fullConjugateRowMap({ ...params,
        xRange: params.newXRange || params.xRange,
        yRange: numericalYRange }) : null;
    owner.pending = { owner, type,
      params: snapshotEligibleFullParams(params, skipInteriorDispatch || !!conjugateMap),
      skipInteriorDispatch, conjugateMap, resolve, reject, settled: false,
      inFlight: 0, nextTile: 0, tiles: [], regions: [] };
    startPending(owner);
    pump();
  });
}

export function useFractalWorker() {
  const ownerRef = useRef(null);
  useEffect(() => {
    const owner = { active: null, pending: null };
    ownerRef.current = owner;
    owners.add(owner);
    return () => {
      ownerRef.current = null;
      cancelOwner(owner, abortError('Canvas unmounted'));
      owners.delete(owner);
      if (owners.size === 0) {
        stopPool(new Error('Workers terminated'));
        workerFailed = false;
      }
    };
  }, []);

  const renderFull = useCallback(params => requestRender(ownerRef.current, 'renderFull', params), []);
  const renderPan = useCallback(params => requestRender(ownerRef.current, 'renderPan', params), []);
  const renderExactPan = useCallback(params => requestRender(ownerRef.current, 'renderExactPan', params), []);
  const cancelRender = useCallback(() => cancelOwner(ownerRef.current, abortError()), []);
  const isWorkerAvailable = useCallback(() => !workerFailed && canUseWorkers(), []);
  const shouldUseWorker = useCallback((width, height) => {
    return width * height >= MIN_PIXELS_FOR_WORKER && isWorkerAvailable();
  }, [isWorkerAvailable]);
  return { renderFull, renderPan, renderExactPan, cancelRender, isWorkerAvailable, shouldUseWorker };
}

/**
 * Parse a colorFunc to extract serializable parameters for the worker
 * Since functions can't be sent to workers, we need to serialize them
 */
export function serializeColorFunc(colorFunc) {
  // Get the function source to analyze it
  const funcStr = colorFunc.toString();
  
  // Default values
  const params = {
    r: 255,
    gScale: 7,
    b: 0,
    aScale: 15,
    aFixed: null,
    useMu: true
  };

  // Check if it uses numIterations instead of mu
  if (funcStr.includes('numIterations')) {
    params.useMu = false;
  }

  // Extract multipliers using regex
  // Pattern: mu * N or numIterations * N
  const gScaleMatch = funcStr.match(/(?:mu|numIterations)\s*\*\s*(\d+)/);
  if (gScaleMatch) {
    params.gScale = parseInt(gScaleMatch[1], 10);
  }

  // Look for alpha scale - usually the second multiplier or a fixed value
  // Pattern: [ 255, ... , 0, mu * N ] or [ 255, ..., 0, 255 ]
  const alphaMatches = funcStr.match(/,\s*(?:(\d+)|(?:mu|numIterations)\s*\*\s*(\d+))\s*\]/);
  if (alphaMatches) {
    if (alphaMatches[1]) {
      // Fixed alpha value
      params.aFixed = parseInt(alphaMatches[1], 10);
    } else if (alphaMatches[2]) {
      // Alpha scale
      params.aScale = parseInt(alphaMatches[2], 10);
    }
  }

  return params;
}
